<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import { t } from "./lib/i18n.js";

  export let apiBase = "";
  export let apiKey = "";
  export let locale = "en";
  export let onApiError = () => {};

  const JSON_HEADERS = { Accept: "application/json", "Cache-Control": "no-store" };

  let lines = [];
  let loading = false;
  let err = "";
  let lastUpdated = "";
  let limit = 100;
  let paused = false;
  let q = "";

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  function applyFilters(xs) {
    let out = xs || [];
    const needle = (q || "").trim().toLowerCase();
    if (needle) {
      out = out.filter((x) => (x?.line || "").toLowerCase().includes(needle));
    }
    return out;
  }

  async function fetchTail() {
    if (paused) return;
    loading = lines.length === 0;
    err = "";

    try {
      const res = await fetch(`${apiBase}/tail`, {
        method: "GET",
        cache: "no-store",
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        throw new Error("401 unauthorized");
      }
      if (!res.ok) throw new Error(`tail failed: ${res.status}`);

      const data = await res.json();
      const raw = Array.isArray(data?.lines) ? data.lines : [];
      const sliced = raw.slice(0, limit);
      lines = sliced;
      lastUpdated = new Date().toLocaleTimeString([], { hour12: false });
    } catch (e) {
      err = e?.message || String(e);
      onApiError(err);
    } finally {
      loading = false;
    }
  }

  let timer = null;
  function start() {
    stop();
    fetchTail();
    timer = setInterval(fetchTail, 1000);
  }
  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
  }

  onMount(() => {
    start();
  });
  onDestroy(() => {
    stop();
  });

  $: if (!paused && timer == null) start();
  $: if (paused && timer != null) stop();
  $: filteredLines = applyFilters(lines);
</script>

<main class="w-full p-4 sm:p-6">
  <section class="ui-card p-5 sm:p-6">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span class="inline-flex h-9 w-9 items-center justify-center rounded border border-violet-400/60 bg-violet-400/20 text-white">
          <Icon icon="mdi:monitor-dashboard" width="18" height="18" />
        </span>
        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t(locale, "runtime_title")}</h1>
          <p class="mt-0.5 text-sm text-zinc-400 hidden sm:block">{t(locale, "runtime_subtitle")}</p>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <span class="inline-flex items-center gap-2 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300">
          {t(locale, "runtime_limit", { limit })}
        </span>
        <span class="inline-flex items-center gap-2 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300">
          {t(locale, "runtime_updated", { time: lastUpdated || "—" })}
        </span>

        <button
          type="button"
          class="ui-btn inline-flex items-center gap-2"
          onclick={() => fetchTail()}
          title={t(locale, "runtime_refresh_title")}
        >
          <Icon icon={refreshIcon} width="18" height="18" />
          {t(locale, "refresh")}
        </button>

        <button
          type="button"
          class="inline-flex items-center gap-2 border px-3 h-10 text-sm rounded {paused ? 'border-rose-400/30 bg-rose-500/10 text-rose-100' : 'border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-200'}"
          onclick={() => (paused = !paused)}
          title={paused ? t(locale, "runtime_resume_title") : t(locale, "runtime_pause_title")}
        >
          {paused ? t(locale, "live_paused") : t(locale, "runtime_pause")}
        </button>
      </div>
    </div>

    <div class="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
      <div class="ui-card-inner p-4 sm:p-5">
        <div class="text-xs text-zinc-400">{t(locale, "runtime_search")}</div>
        <input
          class="mt-2 w-full rounded border border-white/10 bg-black/30 px-3 h-10 text-sm text-zinc-200 placeholder:text-zinc-600"
          placeholder={t(locale, "runtime_search_placeholder")}
          bind:value={q}
          oninput={() => {}}
        />
        <div class="mt-2 text-[11px] text-zinc-500">{t(locale, "runtime_search_hint")}</div>
      </div>

      <div class="ui-card-inner p-4 sm:p-5">
        <div class="text-xs text-zinc-400">{t(locale, "runtime_limit_label")}</div>
        <input
          type="number"
          min="10"
          max="100"
          step="10"
          class="mt-2 w-full rounded border border-white/10 bg-black/30 px-3 h-10 text-sm text-zinc-200"
          bind:value={limit}
          onchange={() => fetchTail()}
        />
        <div class="mt-2 text-[11px] text-zinc-500">{t(locale, "runtime_limit_hint")}</div>
      </div>
    </div>

    {#if err}
      <div class="mt-4 flex items-start gap-2 rounded border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
        <Icon icon={alertIcon} width="18" height="18" class="mt-[2px]" />
        <div class="min-w-0">
          <div class="font-semibold">{t(locale, "runtime_error_title")}</div>
          <div class="mt-1 text-xs text-rose-200/90 break-words">{err}</div>
        </div>
      </div>
    {/if}

    <div class="mt-5 ui-card-inner overflow-hidden">
      <div class="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div class="text-sm font-semibold text-zinc-200">{t(locale, "runtime_last_lines", { limit })}</div>
        <div class="text-xs text-zinc-500">{loading && !lines.length ? t(locale, "loading") : t(locale, "runtime_shown", { count: filteredLines.length })}</div>
      </div>

      <div class="max-h-[68vh] overflow-auto">
        {#if loading && !lines.length}
          <div class="p-4 text-sm text-zinc-400">{t(locale, "loading")}</div>
        {:else if !filteredLines.length}
          <div class="p-4 text-sm text-zinc-500">{t(locale, "runtime_no_lines")}</div>
        {:else}
          <div class="divide-y divide-white/5">
            {#each filteredLines as it, i (i)}
              <div class="px-4 py-2.5 font-mono text-[12px] leading-snug">
                <div class="flex items-start gap-3">
                  <div class="shrink-0 w-[86px] text-zinc-500" title={it.time_utc}>
                    {it.time_utc || "—"}
                  </div>
                  <span class="shrink-0 inline-flex items-center justify-center rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-zinc-300">
                    {it.stream}
                  </span>
                  <div class="min-w-0 flex-1 break-words text-zinc-200">
                    {it.line}
                  </div>
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </section>
</main>
